import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { register } from "../redux/slices/authSlice";

const CreateEmployeeModal = ({
  employeeData: propEmployeeData,
  isEditMode = false,
  onClose,
}) => {
  // Renamed prop
  const dispatch = useDispatch();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [employeeID, setEmployeeID] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Pre-fill form when in edit mode
  useEffect(() => {
    if (isEditMode && propEmployeeData) {
      // Use renamed prop
      setFullName(propEmployeeData.employeeName || "");
      setEmail(propEmployeeData.email || "");
      setPhone(propEmployeeData.phone || "");
      setEmployeeID(propEmployeeData.id || "");
    } else {
      // Reset form for create mode
      setFullName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setEmployeeID("");
      setError("");
      setSuccess(false);
    }
  }, [isEditMode, propEmployeeData]); // Use renamed prop

  const handleSubmit = async () => {
    if (!fullName || !email || !phone || !password) {
      setError("Please fill in all required fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess(false);

    try {
      const registrationData = {
        // Changed variable name
        name: fullName,
        email: email,
        password: password,
        password_confirmation: confirmPassword,
        phone_number: phone || "1234567890",
        role: "team",
      };

      const result = await dispatch(register(registrationData)).unwrap(); // Use new name

      if (result.success) {
        setSuccess(true);
        // Clear form
        setFullName("");
        setEmail("");
        setPhone("");
        setPassword("");
        setConfirmPassword("");

        // Auto-close after 2 seconds
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      } else {
        setError(result.message || "Failed to add employee");
      }
    } catch (err) {
      if (err.errors) {
        const errorMsg = Object.values(err.errors).flat().join(", ");
        setError(errorMsg);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Something went wrong");
      }
      console.error("Add employee error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFullName("");
    setEmail("");
    setPhone("");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess(false);
  };

  return (
    <div className="announcementModal">
      <div className="row">
        <div className="col-md-12 mt-10 position-relative">
          {error && !success && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          {!success ? (
            <>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  className="form-control mb-10"
                  placeholder="Enter full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  className="form-control mb-10"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  className="form-control mb-10"
                  placeholder="1234567890"
                  value={phone}
                  onChange={(e) => {
                    const numbersOnly = e.target.value.replace(/\D/g, "");
                    setPhone(numbersOnly);
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  className="form-control mb-10"
                  placeholder="Set password for employee"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  className="form-control mb-10"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="d-flex gap-10 mt-20">
                <button
                  className="btn announceButton flex-grow-1"
                  onClick={handleSubmit}
                  disabled={isLoading}>
                  {isLoading ? "Adding Employee..." : "Add Employee"}
                </button>

                <button
                  className="btn btn-secondary flex-grow-1"
                  onClick={handleReset}
                  disabled={isLoading}>
                  Clear
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="mb-3">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                    fill="#28a745"
                  />
                </svg>
              </div>
              <h5 className="text-success">Employee Added Successfully!</h5>
              <p className="text-muted">
                The employee can now log in with their credentials.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEmployeeModal;
